import { useState, useEffect, Fragment, useMemo, useCallback } from "react";
import dayjs from "dayjs";

import {
  Box,
  Grid,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  Paper,
  Divider
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import IosShareIcon from "@mui/icons-material/IosShare";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

import ReportRenderer from "./ReportRenderer";
import SharedReportOverlay from "../../../../modules/reporting/SharedReportOverlay";

import {
  useCachedDisplayData,
  useCachedFirebaseCrud,
  useAccountData
} from "../../../../../helpers/firebase";
import { useUpdateVendorRelationshipFields } from "../../../../../helpers/components/vendors";
import useEmissionsContext from "../../../../../helpers/contexts/emissions";
import { fetchOurApi } from "../../../../../helpers/utils/apiCalls";

const buildAvailableYears = (startDate) => {
  const startYear = dayjs(startDate).year();
  const currentYear = dayjs().year();
  return Array.from(
    { length: currentYear - startYear },
    (_, i) => startYear + i
  );
};

const YearStatusAvatar = ({ isFinalized }) => {
  const { palette } = useTheme();
  const bg = isFinalized ? palette.secondary.main : palette.warning.main;
  const label = isFinalized ? "Finalized" : "Preliminary";
  const Icon = isFinalized ? CheckCircleIcon : WarningIcon;

  return (
    <Tooltip title={label}>
      <Avatar style={{ backgroundColor: bg, width: 28, height: 28 }}>
        <Icon style={{ fontSize: 16 }} />
      </Avatar>
    </Tooltip>
  );
};

const SharedYearRow = ({
  year,
  sharedEntry,
  isFinalized,
  isReadOnly,
  onView,
  onRefresh,
  onUnshare,
  isRefreshing
}) => {
  const { generatedAt, reportUrl, status } = sharedEntry;

  const generatedDate = generatedAt?.toDate
    ? generatedAt.toDate()
    : generatedAt
      ? new Date(generatedAt)
      : null;

  return (
    <Grid container alignItems="center" spacing={1} wrap="nowrap">
      <Grid item>
        <YearStatusAvatar isFinalized={isFinalized} />
      </Grid>
      <Grid item xs>
        <Typography variant="body2">{year}</Typography>
        {generatedDate && (
          <Typography variant="caption" color="textSecondary">
            Generated {dayjs(generatedDate).format("MMM D, YYYY")}
          </Typography>
        )}
      </Grid>
      {status === "reviewed" && (
        <Grid item>
          <Chip label="Reviewed" color="success" size="small" />
        </Grid>
      )}
      {reportUrl && (
        <Grid item>
          <Tooltip title="View report">
            <IconButton size="small" onClick={() => onView(reportUrl)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      )}
      {!isReadOnly && !isFinalized && (
        <Grid item>
          <Tooltip title="Refresh report">
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      )}
      {!isReadOnly && (
        <Grid item>
          <Tooltip title="Remove report">
            <IconButton size="small" color="error" onClick={onUnshare}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      )}
    </Grid>
  );
};

const preliminaryReportSessionKey = (vendorId, year) =>
  `preliminary-report-url-${vendorId}-${year}`;

const YearlyReportsSection = ({
  customer,
  isReadOnly = false,
  onSharedReportsChange
}) => {
  const [{ id: vendorAccountId, startDate }] = useAccountData();
  const [accountingYears] = useCachedDisplayData("accounting");
  const { allEmissions } = useEmissionsContext();
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const updateVendorRelationshipFields =
    useUpdateVendorRelationshipFields("customers");

  const [generatingYear, setGeneratingYear] = useState(null);
  const [refreshingYear, setRefreshingYear] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);

  const { id: relationshipId, sharedReports = [] } = customer;

  const availableYears = isReadOnly
    ? sharedReports.map((r) => r.year).sort((a, b) => a - b)
    : startDate
      ? buildAvailableYears(startDate)
      : [];
  const lockedYears = useMemo(
    () => (accountingYears || []).map(({ date }) => dayjs(date).year()),
    [accountingYears]
  );

  const isYearFinalized = useCallback(
    (year) => lockedYears.includes(year),
    [lockedYears]
  );

  const findAccountingDocForYear = useCallback(
    (year) =>
      (accountingYears || []).find(({ date }) => dayjs(date).year() === year),
    [accountingYears]
  );

  const getCanonicalReportUrl = useCallback(
    (year) => {
      if (isYearFinalized(year)) {
        return findAccountingDocForYear(year)?.reportUrl || null;
      }
      return (
        sessionStorage.getItem(
          preliminaryReportSessionKey(vendorAccountId, year)
        ) || null
      );
    },
    [findAccountingDocForYear, isYearFinalized, vendorAccountId]
  );

  const getSharedEntry = (year) =>
    sharedReports.find((r) => r.year === year) || null;

  useEffect(() => {
    if (isReadOnly) {
      return;
    }
    const hasStale = sharedReports.some(
      (r) => isYearFinalized(r.year) && !r.isFinalized
    );
    if (!hasStale) {
      return;
    }
    const updatedReports = sharedReports.map((r) =>
      isYearFinalized(r.year) && !r.isFinalized
        ? { ...r, isFinalized: true, reportUrl: null }
        : r
    );
    updateVendorRelationshipFields(relationshipId, {
      sharedReports: updatedReports
    });
    onSharedReportsChange && onSharedReportsChange(updatedReports);
  }, [
    lockedYears,
    sharedReports,
    isReadOnly,
    relationshipId,
    isYearFinalized,
    onSharedReportsChange,
    updateVendorRelationshipFields
  ]);

  useEffect(() => {
    if (isReadOnly || generatingYear !== null) {
      return;
    }
    const needsRegen = sharedReports.find((r) => r.sharedAt && !r.reportUrl);
    if (!needsRegen) {
      return;
    }
    const canonicalUrl = getCanonicalReportUrl(needsRegen.year);
    if (!canonicalUrl) {
      setGeneratingYear(needsRegen.year);
      return;
    }
    const updatedReports = sharedReports.map((r) =>
      r.year === needsRegen.year
        ? {
            ...r,
            reportUrl: canonicalUrl,
            generatedAt: r.generatedAt || new Date()
          }
        : r
    );
    updateVendorRelationshipFields(relationshipId, {
      sharedReports: updatedReports
    });
    fetchOurApi({
      path: "/vendors/notify-report-shared",
      method: "POST",
      data: { relationshipId, year: needsRegen.year }
    });
    onSharedReportsChange && onSharedReportsChange(updatedReports);
  }, [
    sharedReports,
    isReadOnly,
    generatingYear,
    getCanonicalReportUrl,
    relationshipId,
    onSharedReportsChange,
    updateVendorRelationshipFields
  ]);

  const onUploadComplete = async (year, url) => {
    const isFinalized = isYearFinalized(year);
    const now = new Date();
    const updatedReports = [
      ...sharedReports.filter((r) => r.year !== year),
      {
        year,
        sharedAt: getSharedEntry(year)?.sharedAt || now,
        generatedAt: now,
        status: "shared",
        reportUrl: url,
        isFinalized
      }
    ];
    const accountingDoc = findAccountingDocForYear(year);
    const canonicalWrites =
      isFinalized && accountingDoc?.id
        ? [
            updateCollectionDoc("accounting", accountingDoc.id, {
              reportUrl: url,
              reportGeneratedAt: now
            })
          ]
        : [];
    if (!isFinalized) {
      sessionStorage.setItem(
        preliminaryReportSessionKey(vendorAccountId, year),
        url
      );
    }
    await Promise.all([
      updateVendorRelationshipFields(relationshipId, {
        sharedReports: updatedReports
      }),
      ...canonicalWrites
    ]);
    fetchOurApi({
      path: "/vendors/notify-report-shared",
      method: "POST",
      data: { relationshipId, year }
    });
    onSharedReportsChange && onSharedReportsChange(updatedReports);
    setGeneratingYear(null);
    setRefreshingYear(null);
  };

  const onUploadError = () => {
    setGeneratingYear(null);
    setRefreshingYear(null);
  };

  const onShare = (year) => setGeneratingYear(year);

  const onRefresh = (year) => {
    setRefreshingYear(year);
    setGeneratingYear(year);
  };

  const onUnshare = async (year) => {
    const updatedReports = sharedReports.filter((r) => r.year !== year);
    await updateVendorRelationshipFields(relationshipId, {
      sharedReports: updatedReports
    });
    onSharedReportsChange && onSharedReportsChange(updatedReports);
  };

  const { vendorName } = customer;

  const { palette } = useTheme();

  return (
    <Box p={2}>
      {viewingEntry && (
        <SharedReportOverlay
          reportUrl={viewingEntry.url}
          year={viewingEntry.year}
          companyName={vendorName}
          isFinalized={
            isReadOnly
              ? (viewingEntry.isFinalized ?? false)
              : isYearFinalized(viewingEntry.year)
          }
          onClose={() => setViewingEntry(null)}
        />
      )}
      {generatingYear !== null && (
        <ReportRenderer
          year={generatingYear}
          emissions={allEmissions}
          onUploaded={(url) => onUploadComplete(generatingYear, url)}
          onError={onUploadError}
        />
      )}
      <Paper
        sx={{
          backgroundColor: palette.backgroundGray.light,
          overflow: "hidden",
          borderRadius: 1
        }}
      >
        <Grid container direction="column" spacing={0}>
          {availableYears.map((year, index) => {
            const sharedEntry = getSharedEntry(year);
            const isFinalized = isReadOnly
              ? (sharedEntry?.isFinalized ?? false)
              : isYearFinalized(year);

            if (sharedEntry) {
              return (
                <Fragment key={year}>
                  {index > 0 && <Divider />}
                  <Grid item>
                    <Box px={2} py={1.5}>
                      <SharedYearRow
                        year={year}
                        sharedEntry={sharedEntry}
                        isFinalized={isFinalized}
                        isReadOnly={isReadOnly}
                        onView={(url) =>
                          setViewingEntry({ year, url, isFinalized })
                        }
                        onRefresh={() => onRefresh(year)}
                        onUnshare={() => onUnshare(year)}
                        isRefreshing={refreshingYear === year}
                      />
                    </Box>
                  </Grid>
                </Fragment>
              );
            }

            if (isReadOnly) {
              return null;
            }

            return (
              <Fragment key={year}>
                {index > 0 && <Divider />}
                <Grid item>
                  <Box px={2} py={1.5}>
                    <Grid
                      container
                      alignItems="center"
                      spacing={1}
                      wrap="nowrap"
                    >
                      <Grid item>
                        <YearStatusAvatar isFinalized={isFinalized} />
                      </Grid>
                      <Grid item xs>
                        <Typography variant="body2">{year}</Typography>
                      </Grid>
                      <Grid item>
                        {generatingYear === year ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Tooltip title="Generate & share report">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onShare(year)}
                            >
                              <IosShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Fragment>
            );
          })}
          {availableYears.length === 0 && (
            <Grid item>
              <Box px={2} py={1.5}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                >
                  No report years available yet.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default YearlyReportsSection;
